import type { OnboardingPayload, OnboardingReadiness, OnboardingRequest, OnboardingRequestSummary } from '../lib/onboarding';
import type {
  ApiResponse,
  AuditLogFilters,
  AuditLogPage,
  Bill,
  BillLineItem,
  BillRun,
  BillRunCoverage,
  BillingPocket,
  BillableMeterV2,
  InsightsProfile,
  IntervalCompleteness,
  PhasorSnapshot,
  MeterTouConfig,
  CreateBillRunRequest,
  Municipality,
  RealmInfo,
  Tariff,
  TariffAssignment,
  TariffBooklet,
  TariffRate,
  TariffRateV2,
  TariffSchedule,
  TariffVersion,
  TOUConsumption,
  TOUPeriod,
  VerificationBill,
  VerificationComparison,
  VirtualMeter,
  WaterRestriction,
  WaterRestrictionInput,
  WaterRestrictionPricedRun,
} from './types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://billing-api.utilsync.cloud';
const PDF_API_BASE = import.meta.env.VITE_PDF_API_URL || 'https://pdf-generator.beau-rossouw.workers.dev';

// Custom event for session expiration
export const SESSION_EXPIRED_EVENT = 'billing:session-expired';

function auditQuery(filters: AuditLogFilters, extra: Record<string, string | undefined> = {}): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...filters, ...extra })) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  return p.toString();
}

class ApiClient {
  private token: string | null = null;
  private realm: string = 'master';

  setAuth(token: string, realm: string) {
    this.token = token;
    this.realm = realm;
  }

  /**
   * Read-only access to the current Keycloak access token. Used by the
   * NFC bridge client to authenticate the localhost WebSocket upgrade
   * with the same trust the rest of billing-api enforces (Keycloak
   * JWKS verification, no bridge-specific signer).
   */
  getToken(): string | null {
    return this.token;
  }

  // Audit a successful NFC tap from the desktop bridge or mobile helper.
  async auditNfcTap(body: {
    transport: 'nfc-1581' | 'nfc-iphone-corenfc' | 'nfc-android-tagdispatch';
    chip_uid: string;
    meter_serial?: string;
    volume_m3?: number;
    raw_response_hex?: string;
  }) {
    return this.request<{ ok: boolean; id: string }>(
      'POST',
      '/api/v2/nfc-bridge/audit',
      body,
    );
  }

  private async apiError(response: Response): Promise<Error> {
    if (response.status === 401) {
      console.log('[ApiClient] 401 received, dispatching session expired event');
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return new Error('Session expired');
    }
    const error = await response.json().catch(() => ({ error: response.statusText }));
    // Preserve the HTTP status and the parsed body on the Error so callers
    // that need a structured failure (e.g. a 409 delete-conflict carrying the
    // list of blocking references) can read them; existing callers still
    // just read `.message`.
    const err = new Error(error.error || `API Error: ${response.status}`) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = error;
    return err;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    realm?: string
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // A per-call realm, for a page acting on another realm's record (onboarding);
      // AppShell resets this.realm on every render, so a page cannot switch it for a loop.
      'X-Realm': realm ?? this.realm,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) throw await this.apiError(response);

    return response.json();
  }

  // Bill Runs
  async getBillRuns(params?: { status?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<BillRun[]>>('GET', `/api/bill-runs${queryStr}`);
  }

  async getBillRun(id: string) {
    return this.request<ApiResponse<BillRun>>('GET', `/api/bill-runs/${id}`);
  }

  async createBillRun(data: CreateBillRunRequest) {
    return this.request<ApiResponse<BillRun>>('POST', '/api/bill-runs', data);
  }

  async approveBillRun(id: string) {
    return this.request<ApiResponse<BillRun>>('POST', `/api/bill-runs/${id}/approve`);
  }

  async rejectBillRun(id: string, reason?: string) {
    return this.request<ApiResponse<BillRun>>('POST', `/api/bill-runs/${id}/reject`, { reason });
  }

  async rerunBillRun(id: string, reason: string) {
    return this.request<ApiResponse<BillRun>>('POST', `/api/bill-runs/${id}/rerun`, { reason });
  }

  async getBillRunFlags(id: string) {
    return this.request<ApiResponse<import('./types').BillRunFlag[]>>('GET', `/api/bill-runs/${id}/flags`);
  }

  async acknowledgeBillRunFlag(billRunId: string, flagId: string, comment: string) {
    return this.request<ApiResponse<import('./types').BillRunFlag>>(
      'POST',
      `/api/bill-runs/${billRunId}/flags/${flagId}/acknowledge`,
      { comment },
    );
  }

  async finalizeBillRun(id: string) {
    return this.request<ApiResponse<BillRun>>('POST', `/api/bill-runs/${id}/finalize`);
  }

  /** A realm's finalized statements for one calendar month (Download Monthly). */
  async getMonthlyStatements(month: string) {
    return this.request<{ data: import('./types').MonthlyStatements }>('GET', `/api/statements/monthly?month=${encodeURIComponent(month)}`);
  }

  async generateBillRunPdfs(id: string) {
    return this.request<{ data: { queued: number } }>('POST', `/api/bill-runs/${id}/generate-pdfs`);
  }

  async getBillRunPdfUrl(billRunId: string): Promise<{ url: string; downloadUrl: string }> {
    const res = await this.request<{ data: { url: string; download_url: string } }>('GET', `/api/bill-runs/${billRunId}/pdf-url`);
    return { url: res.data.url, downloadUrl: res.data.download_url };
  }

  async getBillRunDashboard(billRunId: string) {
    return this.request<{ data: import('./types').BillRunDashboard }>('GET', `/api/bill-runs/${billRunId}/dashboard`);
  }

  async compareBillRun(billRunId: string) {
    return this.request<{ data: Array<{ meter_asset_id: string; prev_consumption: number; current_consumption: number; variance_percent: number }> }>('POST', `/api/bill-runs/${billRunId}/compare`);
  }

  async getBuildingReport(buildingId: string) {
    return this.request<{ data: import('./types').BuildingReport | null }>('GET', `/api/v2/buildings/${buildingId}/report`);
  }

  // --- Scheduled + ad-hoc building reports ---
  async listReportSchedules(buildingId: string) {
    return this.request<{ data: import('./types').ReportSchedule[] }>('GET', `/api/v2/report-schedules?building_asset_id=${encodeURIComponent(buildingId)}`);
  }

  async createReportSchedule(body: import('./types').UpsertReportSchedule) {
    return this.request<{ data: import('./types').ReportSchedule }>('POST', '/api/v2/report-schedules', body);
  }

  async updateReportSchedule(id: string, body: import('./types').UpsertReportSchedule) {
    return this.request<{ data: import('./types').ReportSchedule }>('PUT', `/api/v2/report-schedules/${id}`, body);
  }

  async deleteReportSchedule(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/report-schedules/${id}`);
  }

  // Dispatch an existing schedule immediately (does not change its next run).
  async runReportScheduleNow(id: string) {
    return this.request<{ success: boolean; send_log_id: string }>('POST', `/api/v2/report-schedules/${id}/run-now`);
  }

  // Ad-hoc "Send now" — email the current report without a saved schedule.
  // Optional window basis mirrors the schedule fields (default = billing period).
  async sendBuildingReportNow(
    buildingId: string,
    recipients: string[],
    opts?: { basis?: 'billing' | 'window'; frequency?: string; window_start_min?: number; window_end_min?: number },
  ) {
    return this.request<{ success: boolean; send_log_id: string }>('POST', `/api/v2/buildings/${buildingId}/report/send`, { recipients, ...opts });
  }

  async getBuildingCommonArea(buildingId: string) {
    return this.request<{ data: import('./types').BuildingCommonArea[] }>('GET', `/api/v2/buildings/${buildingId}/common-area`);
  }

  async addBuildingCommonArea(buildingId: string, data: { common_meter_asset_id: string; description?: string }) {
    return this.request<{ data: import('./types').BuildingCommonArea }>('POST', `/api/v2/buildings/${buildingId}/common-area`, data);
  }

  async removeBuildingCommonArea(buildingId: string, caId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/buildings/${buildingId}/common-area/${caId}`);
  }

  // Bills
  async getBills(params?: { bill_run_id?: string; meter_asset_id?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.bill_run_id) query.set('bill_run_id', params.bill_run_id);
    if (params?.meter_asset_id) query.set('meter_asset_id', params.meter_asset_id);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<Bill[]>>('GET', `/api/bills${queryStr}`);
  }

  async getBill(id: string) {
    return this.request<ApiResponse<Bill>>('GET', `/api/bills/${id}`);
  }

  async getBillLineItems(billId: string) {
    return this.request<ApiResponse<BillLineItem[]>>('GET', `/api/bills/${billId}/line-items`);
  }

  async getBillTOUConsumption(billId: string) {
    return this.request<ApiResponse<TOUConsumption[]>>('GET', `/api/bills/${billId}/tou-consumption`);
  }

  // Tariffs
  async getTariffs(params?: { active?: boolean; rate_type?: string }) {
    const query = new URLSearchParams();
    if (params?.active !== undefined) query.set('active', params.active.toString());
    if (params?.rate_type) query.set('rate_type', params.rate_type);
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<TariffSchedule[]>>('GET', `/api/tariffs${queryStr}`);
  }

  async getTariff(id: string) {
    return this.request<ApiResponse<TariffSchedule>>('GET', `/api/tariffs/${id}`);
  }

  async getTariffRates(tariffId: string) {
    return this.request<ApiResponse<TariffRate[]>>('GET', `/api/tariffs/${tariffId}/rates`);
  }

  // Soft-delete (sets is_active=0). Meters that reference the tariff keep
  // their assignment but the tariff won't appear in active dropdowns.
  async deleteTariff(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/tariffs/${id}`);
  }

  // Assignments
  async getAssignments(params?: { building_asset_id?: string; meter_asset_id?: string }) {
    const query = new URLSearchParams();
    if (params?.building_asset_id) query.set('building_asset_id', params.building_asset_id);
    if (params?.meter_asset_id) query.set('meter_asset_id', params.meter_asset_id);
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<TariffAssignment[]>>('GET', `/api/assignments${queryStr}`);
  }

  async createAssignment(data: {
    tariff_schedule_id: string;
    building_asset_id?: string;
    meter_asset_id?: string;
    effective_from: string;
    effective_to?: string;
  }) {
    return this.request<ApiResponse<TariffAssignment>>('POST', '/api/assignments', data);
  }

  async deleteAssignment(id: string) {
    return this.request<void>('DELETE', `/api/assignments/${id}`);
  }

  // Meters
  async getBillableMeters() {
    return this.request<ApiResponse<BillableMeter[]>>('GET', '/api/meters/billable');
  }

  // PDF Operations (via pdf-generator worker)
  async generateBillPdf(billId: string) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Realm': this.realm,
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${PDF_API_BASE}/api/bills/${billId}/pdf`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `PDF Error: ${response.status}`);
    }

    return response.json() as Promise<{ pdf_url: string }>;
  }

  async getBillPdfUrl(billId: string): Promise<{ url: string; downloadUrl: string }> {
    const res = await this.request<{ data: { url: string; download_url: string } }>('GET', `/api/bills/${billId}/pdf-url`);
    return { url: res.data.url, downloadUrl: res.data.download_url };
  }

  private async requestFormData<T>(path: string, formData: FormData): Promise<T> {
    const headers: Record<string, string> = {
      'X-Realm': this.realm,
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        throw new Error('Session expired');
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `API Error: ${response.status}`);
    }

    return response.json();
  }

  // ============================================================================
  // V2: Tariffs (hierarchy)
  // ============================================================================

  async getTariffsV2(params?: { municipality_id?: string; is_active?: boolean }) {
    const query = new URLSearchParams();
    if (params?.municipality_id) query.set('municipality_id', params.municipality_id);
    if (params?.is_active !== undefined) query.set('is_active', params.is_active.toString());
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<Tariff[]>>('GET', `/api/v2/tariffs${queryStr}`);
  }

  async getTariffV2(id: string) {
    return this.request<ApiResponse<Tariff & { versions: TariffVersion[] }>>('GET', `/api/v2/tariffs/${id}`);
  }

  async createTariffV2(data: { municipality_id?: string; name: string; description?: string; category?: string; rate_type: 'simple' | 'tou'; restriction_exempt?: boolean }) {
    return this.request<ApiResponse<Tariff>>('POST', '/api/v2/tariffs', data);
  }

  /**
   * `municipality_id`: send ONLY when it changed; null = global. A TOU tariff
   * whose holiday remaps differ between the old and new municipality is
   * refused with 409 `code: 'holiday_pricing_change'` (the thrown Error
   * carries `status` and `body`) unless `acknowledge_holiday_change` is true.
   * The rule lives in billing-api; callers show the API's sentence.
   */
  async updateTariffV2(id: string, data: Partial<{
    name: string;
    description: string;
    category: string;
    is_active: boolean;
    municipality_id: string | null;
    acknowledge_holiday_change: boolean;
    restriction_exempt: boolean;
  }>) {
    return this.request<ApiResponse<Tariff>>('PUT', `/api/v2/tariffs/${id}`, data);
  }

  async deleteTariffV2(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/tariffs/${id}`);
  }

  // Tariff Versions
  async getTariffVersions(tariffId: string) {
    return this.request<ApiResponse<TariffVersion[]>>('GET', `/api/v2/tariffs/${tariffId}/versions`);
  }

  async getTariffVersion(tariffId: string, versionId: string) {
    return this.request<ApiResponse<TariffVersion & { rates: TariffRateV2[]; tou_periods: TOUPeriod[] }>>('GET', `/api/v2/tariffs/${tariffId}/versions/${versionId}`);
  }

  async createTariffVersion(tariffId: string, data: {
    financial_year: string;
    effective_from: string;
    effective_to?: string;
    is_gazetted?: boolean;
    gazetted_date?: string;
    booklet_id?: string;
    rates?: Array<{ charge_type: string; tou_bucket?: string; season?: string; rate_cents: number; threshold_value?: number; unit?: string; description?: string; volume_percent?: number }>;
    tou_periods?: Array<{ bucket: string; day_type: string; season: string; start_hour: number; start_minute?: number; end_hour: number; end_minute?: number }>;
    /** TV-4: explicit "save it anyway" for a chain gap this version's dates would leave. Never rescues an overlap. */
    acknowledge_gap?: boolean;
  }) {
    return this.request<ApiResponse<TariffVersion>>('POST', `/api/v2/tariffs/${tariffId}/versions`, data);
  }

  async updateTariffVersion(tariffId: string, versionId: string, data: {
    effective_from?: string;
    effective_to?: string;
    is_gazetted?: boolean;
    gazetted_date?: string;
    booklet_id?: string;
    is_active?: boolean;
    rates?: Array<{ charge_type: string; tou_bucket?: string; season?: string; rate_cents: number; threshold_value?: number; unit?: string; description?: string; volume_percent?: number }>;
    tou_periods?: Array<{ bucket: string; day_type: string; season: string; start_hour: number; start_minute?: number; end_hour: number; end_minute?: number }>;
    /** TV-4: see createTariffVersion. */
    acknowledge_gap?: boolean;
  }) {
    return this.request<ApiResponse<TariffVersion>>('PUT', `/api/v2/tariffs/${tariffId}/versions/${versionId}`, data);
  }

  async gazetteTariffVersion(tariffId: string, versionId: string, gazetted_date?: string) {
    return this.request<ApiResponse<TariffVersion>>('POST', `/api/v2/tariffs/${tariffId}/versions/${versionId}/gazette`, { gazetted_date });
  }

  // Tariff Booklets
  async getBooklets(params?: { municipality_id?: string; financial_year?: string }) {
    const query = new URLSearchParams();
    if (params?.municipality_id) query.set('municipality_id', params.municipality_id);
    if (params?.financial_year) query.set('financial_year', params.financial_year);
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<TariffBooklet[]>>('GET', `/api/v2/booklets${queryStr}`);
  }

  async uploadBooklet(file: File, municipalityId: string, financialYear: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('municipality_id', municipalityId);
    formData.append('financial_year', financialYear);
    return this.requestFormData<ApiResponse<TariffBooklet>>('/api/v2/booklets', formData);
  }

  async getBookletUrl(id: string) {
    return this.request<ApiResponse<{ url: string; expires_at: string }>>('GET', `/api/v2/booklets/${id}/url`);
  }

  async deleteBooklet(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/booklets/${id}`);
  }

  // Billing Pockets
  async getPockets(params?: { building_asset_id?: string }) {
    const query = new URLSearchParams();
    if (params?.building_asset_id) query.set('building_asset_id', params.building_asset_id);
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request<ApiResponse<BillingPocket[]>>('GET', `/api/v2/pockets${queryStr}`);
  }

  async getPocket(id: string) {
    return this.request<ApiResponse<BillingPocket>>('GET', `/api/v2/pockets/${id}`);
  }

  async createPocket(data: { building_asset_id: string; name: string; description?: string; shop_no?: string }) {
    return this.request<ApiResponse<BillingPocket>>('POST', '/api/v2/pockets', data);
  }

  async updatePocket(id: string, data: Partial<{
    name: string;
    description: string;
    shop_no: string;
    account_number: string;
    /** @deprecated Pass ca_percent_kwh + ca_percent_kl instead. */
    ca_percent: number;
    ca_percent_kwh: number;
    ca_percent_kl: number;
  }>) {
    return this.request<ApiResponse<BillingPocket>>('PUT', `/api/v2/pockets/${id}`, data);
  }

  async deletePocket(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/pockets/${id}`);
  }

  async addPocketMeters(pocketId: string, meterAssetIds: string[]) {
    return this.request<ApiResponse<{ added: string[]; count: number }>>('POST', `/api/v2/pockets/${pocketId}/meters`, { meter_asset_ids: meterAssetIds });
  }

  async removePocketMeter(pocketId: string, meterAssetId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/pockets/${pocketId}/meters/${meterAssetId}`);
  }

  // Tenants
  async getTenants(params?: { pocket_id?: string; building_asset_id?: string; include_history?: boolean }) {
    const q = new URLSearchParams();
    if (params?.pocket_id) q.set('pocket_id', params.pocket_id);
    if (params?.building_asset_id) q.set('building_asset_id', params.building_asset_id);
    if (params?.include_history) q.set('include_history', '1');
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.request<ApiResponse<import('./types').PocketTenant[]>>('GET', `/api/v2/tenants${qs}`);
  }

  async getTenant(id: string) {
    return this.request<ApiResponse<import('./types').PocketTenant>>('GET', `/api/v2/tenants/${id}`);
  }

  async createTenant(data: {
    pocket_id: string;
    trading_name: string;
    registered_name?: string;
    account_number?: string;
    start_date: string;
    end_date?: string | null;
  }) {
    return this.request<ApiResponse<import('./types').PocketTenant>>('POST', '/api/v2/tenants', data);
  }

  async updateTenant(id: string, data: Partial<{
    trading_name: string;
    registered_name: string | null;
    account_number: string | null;
    start_date: string;
    end_date: string | null;
  }>) {
    return this.request<ApiResponse<import('./types').PocketTenant>>('PUT', `/api/v2/tenants/${id}`, data);
  }

  async deleteTenant(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/v2/tenants/${id}`);
  }

  // Meter Tariff Assignment (v2)
  async setMeterTariff(assetId: string, tariffId: string) {
    return this.request<ApiResponse<unknown>>('PUT', `/api/v2/meters/${assetId}/tariff`, { tariff_id: tariffId });
  }

  async getBillableMetersV2() {
    return this.request<ApiResponse<BillableMeterV2[]>>('GET', '/api/v2/meters/billable');
  }

  // Insights dashboard — priced, TOU-bucketed 30-min interval profile for one
  // or more meters over a window. `from`/`to` are ISO instants.
  async getInsightsProfile(params: { meters: string[]; from: string; to: string }) {
    const query = new URLSearchParams({
      meters: params.meters.join(','),
      from: params.from,
      to: params.to,
    });
    return this.request<ApiResponse<InsightsProfile>>('GET', `/api/v2/insights/profile?${query.toString()}`);
  }

  // Insights dashboard — per-meter, per-SAST-day 30-min interval completeness
  // (counts of the demand-calc Status datapoints, bucketed by provenance).
  async getIntervalCompleteness(params: { meters: string[]; from: string; to: string }) {
    const query = new URLSearchParams({
      meters: params.meters.join(','),
      from: params.from,
      to: params.to,
    });
    return this.request<ApiResponse<IntervalCompleteness>>('GET', `/api/v2/insights/interval-completeness?${query.toString()}`);
  }

  // Phasor snapshot for one meter at an instant (or the latest reading).
  async getPhasor(params: { meter: string; at?: string }) {
    const query = new URLSearchParams({ meter: params.meter });
    if (params.at) query.set('at', params.at);
    return this.request<ApiResponse<PhasorSnapshot>>('GET', `/api/v2/insights/phasor?${query.toString()}`);
  }

  // TOU register config (per-meter)
  async listMeterRegisters(assetId: string) {
    return this.request<ApiResponse<{ registers: string[] }>>('GET', `/api/v2/meters/${assetId}/registers`);
  }

  async getMeterTouConfig(assetId: string) {
    return this.request<ApiResponse<MeterTouConfig | null>>('GET', `/api/v2/meters/${assetId}/tou-config`);
  }

  async putMeterTouConfig(assetId: string, config: {
    tou_peak_register?: string | null;
    tou_standard_register?: string | null;
    tou_offpeak_register?: string | null;
    md_register?: string | null;
    // PR 1d — Meter Capacity & NMD inputs.
    breaker_size_amps?: number | null;
    breaker_phases?: number;
    voltage_class?: 'LV' | 'MV' | null;
    voltage_value?: number | null;
    nmd_kva?: number | null;
    md_is_measured?: boolean;
  }) {
    return this.request<ApiResponse<MeterTouConfig>>('PUT', `/api/v2/meters/${assetId}/tou-config`, config);
  }

  async refreshMeterRegisters(assetId: string, registers?: string[]) {
    return this.request<ApiResponse<{ readings: Record<string, { value: unknown; read_at: string }> }>>(
      'POST',
      `/api/v2/meters/${assetId}/registers/refresh`,
      registers ? { registers } : {},
    );
  }

  async copyMeterTouConfig(targetAssetId: string, sourceAssetId: string, fields?: string[]) {
    return this.request<ApiResponse<MeterTouConfig>>(
      'POST',
      `/api/v2/meters/${targetAssetId}/tou-config/copy-from`,
      { source_meter_asset_id: sourceAssetId, fields },
    );
  }

  async calibrateMeterRegisters(assetId: string) {
    return this.request<ApiResponse<{
      proposal: {
        peak?: string;
        standard?: string;
        off_peak?: string;
        confidences: Record<string, number>;
      } | null;
      raw?: Record<string, { peak: number; standard: number; off_peak: number; total: number }>;
      reason?: string;
    }>>('POST', `/api/v2/meters/${assetId}/tou-config/calibrate`, {});
  }

  // ---------------------------------------------------------------------------
  // PR 2 — Virtual Meters
  // ---------------------------------------------------------------------------
  async listVirtualMeters() {
    return this.request<ApiResponse<VirtualMeter[]>>('GET', '/api/v2/virtual-meters');
  }

  async getVirtualMeter(id: string) {
    return this.request<ApiResponse<VirtualMeter>>('GET', `/api/v2/virtual-meters/${id}`);
  }

  async createVirtualMeter(payload: {
    meter_serial: string;
    parent_id: string | null;
    sources: Array<{ source_meter_asset_id: string; operation: '+' | '-'; fraction: number }>;
  }) {
    return this.request<ApiResponse<{ id: string; meter_asset_id: string; meter_serial: string }>>(
      'POST', '/api/v2/virtual-meters', payload,
    );
  }

  async patchVirtualMeterSources(id: string, sources: Array<{ source_meter_asset_id: string; operation: '+' | '-'; fraction: number }>) {
    return this.request<ApiResponse<{ ok: boolean }>>(
      'PATCH', `/api/v2/virtual-meters/${id}/sources`, { sources },
    );
  }

  async deleteVirtualMeter(id: string) {
    return this.request<ApiResponse<{ ok: boolean; or_asset_id: string }>>(
      'DELETE', `/api/v2/virtual-meters/${id}`,
    );
  }

  // Buildings (from the IoT portal)
  async getBuildings() {
    return this.request<ApiResponse<{ id: string; name: string }[]>>('GET', '/api/v2/buildings');
  }

  // Municipalities
  async getMunicipalities() {
    return this.request<ApiResponse<Municipality[]>>('GET', '/api/municipalities');
  }

  async createMunicipality(name: string, province?: string) {
    return this.request<{ data: Municipality }>('POST', '/api/municipalities', { name, province });
  }

  // A blocked delete rejects with 409; the thrown Error carries `.status` and
  // `.body` (a MunicipalityDeleteConflict) so the caller can list the blockers.
  async deleteMunicipality(id: string) {
    return this.request<{ success: boolean; cleaned: import('./types').MunicipalityDeleteCleaned }>(
      'DELETE',
      `/api/municipalities/${id}`,
    );
  }

  // Water restriction entries (billing:admin). A 409 attaches the parsed body
  // (a WaterRestrictionRefusal) as `.body`, like deleteMunicipality.
  async createWaterRestriction(municipalityId: string, body: WaterRestrictionInput) {
    return this.request<{ data: WaterRestriction; pending_runs: WaterRestrictionPricedRun[] }>('POST', `/api/municipalities/${encodeURIComponent(municipalityId)}/restrictions`, body);
  }

  async updateWaterRestriction(municipalityId: string, restrictionId: string, body: WaterRestrictionInput) {
    return this.request<{ data: WaterRestriction; pending_runs: WaterRestrictionPricedRun[] }>(
      'PUT', `/api/municipalities/${encodeURIComponent(municipalityId)}/restrictions/${encodeURIComponent(restrictionId)}`, body);
  }

  async deleteWaterRestriction(municipalityId: string, restrictionId: string, acknowledgePricedRuns = false) {
    const q = acknowledgePricedRuns ? '?acknowledge_priced_runs=1' : '';
    return this.request<{ success: boolean; pending_runs: WaterRestrictionPricedRun[] }>(
      'DELETE', `/api/municipalities/${encodeURIComponent(municipalityId)}/restrictions/${encodeURIComponent(restrictionId)}${q}`);
  }


  // Bill Runs (v2 - with pocket support)
  async createBillRunV2(data: CreateBillRunRequest) {
    return this.request<ApiResponse<BillRun>>('POST', '/api/v2/bill-runs', data);
  }

  // TV-3 — would a bill run over this selection skip any meter? Answered with
  // bill-calculator's own rule and taxonomy (see BillRunCoverage). Comma-joined
  // pocket ids, matching the Insights selection reads above; the endpoint
  // realm-scopes off the X-Realm header this client already sends.
  async getBillRunCoverage(params: { pocketIds: string[]; periodStart: string; periodEnd: string }) {
    const query = new URLSearchParams({
      pockets: params.pocketIds.join(','),
      period_start: params.periodStart,
      period_end: params.periodEnd,
    });
    return this.request<ApiResponse<BillRunCoverage>>('GET', `/api/v2/bill-runs/coverage?${query.toString()}`);
  }

  async sendBillEmail(billId: string, email?: string) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Realm': this.realm,
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${PDF_API_BASE}/api/bills/${billId}/email`, {
      method: 'POST',
      headers,
      body: email ? JSON.stringify({ email }) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Email Error: ${response.status}`);
    }

    return response.json() as Promise<{ message: string }>;
  }

  // ============================================================================
  // Bill Verification
  // ============================================================================

  // Admin endpoints
  async getRealms() {
    return this.request<ApiResponse<RealmInfo[]>>('GET', '/api/admin/realms');
  }

  currentRealm(): string {
    return this.realm;
  }

  async getAuditLogs(filters: AuditLogFilters, opts: { cursor?: string | null; limit?: number } = {}): Promise<AuditLogPage> {
    const qs = auditQuery(filters, { cursor: opts.cursor ?? undefined, limit: opts.limit ? String(opts.limit) : undefined });
    return this.request<AuditLogPage>('GET', `/api/admin/audit-logs?${qs}`);
  }

  async downloadAuditExport(filters: AuditLogFilters, format: 'csv' | 'json'): Promise<{ blob: Blob; filename: string }> {
    const headers: Record<string, string> = { 'X-Realm': this.realm };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(`${API_BASE}/api/admin/audit-logs/export?${auditQuery(filters, { format })}`, { headers });
    if (!response.ok) throw await this.apiError(response);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const named = /filename="([^"]+)"/.exec(disposition)?.[1];
    const label = filters.realm === '*' ? 'all-realms' : filters.realm;
    const filename = named ?? `audit-log_${label}_${new Date().toISOString().slice(0, 10)}.${format}`;
    return { blob: await response.blob(), filename };
  }

  async getVerificationBills(params?: { status?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return this.request<ApiResponse<VerificationBill[]>>('GET', `/api/v2/verification-bills${qs ? '?' + qs : ''}`);
  }

  async uploadVerificationBill(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.requestFormData<ApiResponse<VerificationBill>>('/api/v2/verification-bills', formData);
  }

  async getVerificationBill(id: string) {
    return this.request<ApiResponse<VerificationBill>>('GET', `/api/v2/verification-bills/${id}`);
  }

  async confirmVerificationBill(id: string, data: Partial<VerificationBill>) {
    return this.request<ApiResponse<VerificationBill>>('PUT', `/api/v2/verification-bills/${id}/confirm`, data);
  }

  async rejectVerificationBill(id: string, reason?: string) {
    return this.request<ApiResponse<VerificationBill>>('POST', `/api/v2/verification-bills/${id}/reject`, { reason });
  }

  async getVerificationComparison(verificationBillId: string) {
    return this.request<ApiResponse<VerificationComparison[]>>('GET', `/api/v2/verification-bills/${verificationBillId}/comparison`);
  }

  // ============================================================================
  // Field Tools — NFC + OCR commissioning and manual readings
  // ============================================================================

  // ----- Cross-realm meter search by serial -----
  async searchMetersBySerial(serial: string) {
    const qs = new URLSearchParams({ serial }).toString();
    return this.request<ApiResponse<MeterSearchMatch[]>>('GET', `/api/v2/meters/search?${qs}`);
  }

  // ----- Manual readings: OCR -----
  async ocrManualReading(image: File) {
    const fd = new FormData();
    fd.append('image', image);
    return this.requestFormData<{
      extracted: ManualReadingOcrExtraction;
      image_id: string;
      image_r2_key: string;
      error?: string;
      detail?: string;
    }>('/api/v2/manual-reading/ocr', fd);
  }

  // ----- Manual readings: submit -----
  async submitManualReading(body: ManualReadingSubmitBody) {
    return this.request<{
      ok: boolean;
      status: 'committed' | 'pending_approval' | 'failed';
      record_id: string;
      written?: string[];
      or_response_summary?: string;
    }>('POST', '/api/v2/manual-reading/submit', body);
  }

  async getPendingManualReadings() {
    return this.request<{
      data: ManualReadingRecord[];
      // Map of `${realm}::${serial}` → existing pending_review commissioning_record id.
      // Lets the SPA mark sibling rows for the same missing meter so admins
      // don't keep clicking Hand off and creating duplicates.
      awaiting_commissioning?: Record<string, string>;
      // Live existence check at request time — keyed by serial, value is the
      // list of realms where the meter currently exists. Overrides the cached
      // `serial_check` in source_payload (which can go stale in both
      // directions). When `existing_serials_ok` is false the live check
      // failed (Hyperdrive blip) and the SPA should fall back to cached.
      existing_serials?: Record<string, string[]>;
      existing_serials_ok?: boolean;
    }>('GET', '/api/v2/manual-reading/pending');
  }

  async getMyManualReadings() {
    return this.request<ApiResponse<ManualReadingRecord[]>>('GET', '/api/v2/manual-reading/mine');
  }

  async approveManualReading(
    id: string,
    opts?: {
      override_values?: Record<string, number>;
      asset_id?: string;
      realm?: string;
      asset_type?: string;
      /**
       * The checks_digest of the held checks the reviewer was shown. The
       * server writes only if it still matches what is held now.
       */
      confirm_checks?: string;
    },
  ) {
    // A held reading answers 409 { error: 'reading_checks', message, checks };
    // the thrown error carries that body (see apiError).
    return this.request<{
      ok: boolean;
      status: string;
      data: ManualReadingRecord | null;
      or_response_summary?: string;
      translation?: TranslationStep[];
      /** The same value was already stored at that time; nothing was written. */
      already_recorded?: boolean;
    }>('POST', `/api/v2/manual-reading/${id}/approve`, opts || {});
  }

  /** The reading checks for every pending row (an early warning; approval re-runs them). */
  async getPendingReadingChecks() {
    return this.request<{ data: Record<string, PendingReadingChecks> }>('GET', '/api/v2/manual-reading/pending/checks');
  }

  /** Every file received through reading@, commission@ and Field Tools (billing:audit only). */
  async getUploads(days?: number) {
    const qs = days ? `?days=${days}` : '';
    return this.request<{ data: UploadListRow[]; days: number }>('GET', `/api/v2/uploads${qs}`);
  }

  /**
   * A reading's or commission's original upload, under the name it was sent
   * with. The server's Content-Disposition carries the exact name
   * (filename*=UTF-8''…, exposed by CORS); `fallbackName` covers a response
   * without one.
   */
  async downloadOriginal(kind: UploadKind, id: string, fallbackName = 'original'): Promise<{ blob: Blob; filename: string }> {
    const path = kind === 'reading'
      ? `/api/v2/manual-reading/${encodeURIComponent(id)}/original`
      : `/api/v2/commissioning/records/${encodeURIComponent(id)}/original`;
    const headers: Record<string, string> = { 'X-Realm': this.realm };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(`${API_BASE}${path}`, { headers });
    if (!response.ok) throw await this.apiError(response);
    return { blob: await response.blob(), filename: filenameFromDisposition(response.headers.get('Content-Disposition')) ?? fallbackName };
  }

  async rejectManualReading(id: string, reason?: string) {
    return this.request<{
      ok: boolean;
      status: string;
      data: ManualReadingRecord | null;
    }>('POST', `/api/v2/manual-reading/${id}/reject`, { reason });
  }

  async handoffManualReadingToCommissioning(
    id: string,
    body?: { asset_type?: 'ElectricityConsumerAsset' | 'GroundwaterSensorAsset'; building_hint?: string },
  ) {
    return this.request<{
      ok: boolean;
      commissioning_record_id: string;
      asset_type: string;
      deduped: boolean;
    }>('POST', `/api/v2/manual-reading/${id}/handoff-to-commissioning`, body || {});
  }

  // ----- Commissioning: OCR -----
  async ocrCommissioning(image: File) {
    const fd = new FormData();
    fd.append('image', image);
    return this.requestFormData<{
      extracted: CommissioningOcrExtraction;
      image_id: string;
      image_r2_key: string;
      error?: string;
      detail?: string;
    }>('/api/v2/commissioning/ocr', fd);
  }

  // ----- Commissioning: finalize -----
  async finalizeCommissioning(body: CommissioningFinalizeBody, realm?: string) {
    return this.request<{
      ok: boolean;
      asset_id: string;
      record_id: string;
      needs_cf_wiring: boolean;
      warnings: string[];
    }>('POST', '/api/v2/commissioning/finalize', body, realm);
  }

  // ----- Commissioning records (recent + needs-cf-wiring queue) -----
  async listCommissioningRecords(opts?: { needsCfWiring?: boolean }) {
    const qs = opts?.needsCfWiring ? '?needs_cf_wiring=1' : '';
    return this.request<ApiResponse<CommissioningRecord[]>>('GET', `/api/v2/commissioning/records${qs}`);
  }

  // Onboarding intake (spec 2026-10-29). Calls on one request name its realm.
  async listOnboardingRequests() {
    return this.request<ApiResponse<OnboardingRequestSummary[]>>('GET', '/api/v2/onboarding/requests');
  }
  async getOnboardingRequest(id: string, realm: string) {
    return this.request<ApiResponse<OnboardingRequest>>('GET', `/api/v2/onboarding/requests/${encodeURIComponent(id)}`, undefined, realm);
  }
  async createOnboardingRequest(payload: OnboardingPayload) {
    return this.request<ApiResponse<OnboardingRequest>>('POST', '/api/v2/onboarding/requests', payload, payload.realm);
  }
  async updateOnboardingRequest(id: string, payload: OnboardingPayload) {
    return this.request<ApiResponse<OnboardingRequest>>('PUT', `/api/v2/onboarding/requests/${encodeURIComponent(id)}`, payload, payload.realm);
  }
  async submitOnboardingRequest(id: string, realm: string) {
    return this.request<{ data: OnboardingRequest; email: 'sent' | 'skipped' | 'failed' }>(
      'POST', `/api/v2/onboarding/requests/${encodeURIComponent(id)}/submit`, {}, realm);
  }
  async getOnboardingReadiness(id: string, realm: string) {
    return this.request<ApiResponse<OnboardingReadiness>>('GET', `/api/v2/onboarding/requests/${encodeURIComponent(id)}/readiness`, undefined, realm);
  }

  async markCommissioningCfWired(id: string) {
    return this.request<{ ok: boolean }>('POST', `/api/v2/commissioning/records/${id}/cf-wired`);
  }

  // ----- Pending email-in commissioning queue -----
  async getPendingCommissioning() {
    return this.request<ApiResponse<PendingCommissioning[]>>('GET', '/api/v2/commissioning/pending');
  }

  /** Retire a committed commission whose Portal asset was deleted, so the
   *  serial can be commissioned again. `dry_run` answers with the effects. */
  async retireCommissioning(id: string, body: { dry_run?: boolean; reason?: string }) {
    return this.request<RetireCommissionResult>('POST', `/api/v2/commissioning/records/${id}/retire`, body);
  }

  async rejectPendingCommissioning(id: string, reason?: string) {
    return this.request<{ ok: boolean }>('POST', `/api/v2/commissioning/records/${id}/reject`, { reason });
  }

  // ----- Parent asset search (for the commission picker) -----
  async searchParentAssets(q: string, realm?: string) {
    const params = new URLSearchParams({ q });
    if (realm) params.set('realm', realm);
    return this.request<ApiResponse<ParentAssetMatch[]>>('GET', `/api/v2/assets/parent-search?${params.toString()}`);
  }

  // ----- Direct children of an asset (for drilling Building → Group) -----
  async getAssetChildren(assetId: string) {
    return this.request<{
      data: ParentAssetMatch[];
      self: ParentAssetMatch;
    }>('GET', `/api/v2/assets/${encodeURIComponent(assetId)}/children`);
  }

  // ----- Commissioning profiles -----
  async listCommissioningProfiles() {
    return this.request<ApiResponse<CommissioningProfile[]>>('GET', '/api/v2/commissioning/profiles');
  }

  async createCommissioningProfile(body: Omit<CommissioningProfile, 'id' | 'realm' | 'created_at'>) {
    return this.request<ApiResponse<CommissioningProfile>>('POST', '/api/v2/commissioning/profiles', body);
  }

  async deleteCommissioningProfile(id: string) {
    return this.request<{ ok: boolean }>('DELETE', `/api/v2/commissioning/profiles/${id}`);
  }
}

// ============================================================================
// Field Tools types
// ============================================================================

export interface MeterSearchMatch {
  asset_id: string;
  asset_type: string;
  realm: string;
  parent_id: string | null;
  name: string;
  // Ancestor chain ordered root → immediate parent. Empty when the meter has
  // no parent. The duplicate-picker renders this as a breadcrumb so the admin
  // can disambiguate by building / site instead of just an opaque asset id.
  parent_path: Array<{ id: string; name: string; type: string }>;
}

// Translation step returned by the approve endpoint — describes how a stored
// value was mapped to the Portal attribute that actually got written. For water
// meters the m³ → L ×1000 conversion shows up as a unit_note.
export interface TranslationStep {
  source_key: string;
  source_value: number;
  target_key: string;
  target_value: number;
  unit_note?: string;
}

export interface ManualReadingOcrExtraction {
  meter_serial?: string | null;
  readings: Record<string, number | null>;
  display_timestamp?: string | null;
  alarm_codes?: string[] | null;
  confidence?: { meter_serial?: number; readings_overall?: number };
}

export interface CommissioningOcrExtraction {
  meter_serial?: string | null;
  meter_type?: 'electricity' | 'water' | 'unknown' | null;
  manufacturer?: string | null;
  model?: string | null;
  ct_ratio?: string | null;
  voltage_class?: string | null;
  phases?: number | null;
  iec_compliance?: string | null;
  year_of_manufacture?: number | null;
  confidence?: Record<string, number>;
}

export interface ManualReadingSubmitBody {
  meter_serial: string;
  asset_id: string;
  realm: string;                  // meter's realm, resolved from /search
  readings: Record<string, number>;
  reading_timestamp: string;      // ISO
  source: 'manual-nfc' | 'manual-ocr';
  source_payload?: unknown;
  image_id?: string;
  image_r2_key?: string;
}

export type UploadKind = 'reading' | 'commissioning';

/** One check a manual reading failed (billing-core readingChecks.ts). */
export type ReadingCheck =
  | { kind: 'would_overwrite'; existing_value: number; new_value: number; existing_source: string | null }
  | { kind: 'below_previous'; previous_value: number; previous_at: number; new_value: number }
  | { kind: 'above_next'; next_value: number; next_at: number; new_value: number }
  | { kind: 'unusual_usage'; implied_daily: number; typical_daily: number; ratio: number };

export interface AttributeCheckResult {
  attribute: string;
  new_value: number;
  already_recorded: boolean;
  checks: ReadingCheck[];
}

export type PendingReadingChecks =
  | {
      status: 'checked';
      asset_id: string;
      realm: string;
      asset_type: string;
      already_recorded: boolean;
      held: boolean;
      attributes: AttributeCheckResult[];
      /** Fingerprint of the held checks; sent back as confirm_checks. */
      checks_digest?: string;
    }
  | { status: 'unresolved'; reason: 'not_found' | 'ambiguous' | 'bad_timestamp' };

/** One file in the Uploads list. */
export interface UploadListRow {
  kind: UploadKind;
  record_id: string;
  filename: string;
  sender: string | null;
  /** UTC, D1 `datetime('now')` form. */
  received_at: string;
  rows: number;
  committed: number;
  pending: number;
  rejected: number;
  other: number;
  realms: string[];
}

/** The exact name from `filename*=UTF-8''…`, else the quoted `filename="…"`. */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (star) {
    try { return decodeURIComponent(star.trim()); } catch { /* fall through */ }
  }
  return /filename="([^"]+)"/i.exec(header)?.[1] ?? null;
}

export interface ManualReadingRecord {
  id: string;
  realm: string;
  asset_id: string | null;
  meter_serial: string;
  reading_timestamp: string;
  captured_at: string;
  submitted_by: string;
  source: 'manual-nfc' | 'manual-ocr' | 'manual-email';
  values_json: Record<string, number> | string;
  source_payload: unknown;
  image_r2_key: string | null;
  status: 'pending_approval' | 'committed' | 'rejected' | 'failed';
  approved_by: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  or_response_summary: string | null;
  error: string | null;
}

export interface CommissioningFinalizeBody {
  meter_serial: string;
  asset_type: string;            // ElectricityConsumerAsset | GroundwaterSensorAsset
  parent_id: string;
  source: 'nfc' | 'ocr' | 'manual' | 'email';
  source_payload?: unknown;
  image_id?: string;
  image_r2_key?: string;
  pending_record_id?: string;
  /** Mains breaker rating in amps (electricity only). Stored on the meter for
   *  Ruraflex/Drakenstein capacity charges and propagated to the OR
   *  `breaker_size` attribute. Leave undefined for water meters. */
  breaker_size_amps?: number;
  /** 1 or 3. Defaults to 3 if unset. */
  breaker_phases?: number;
  /** CT multiplier for a meter not programmed with its ratio (electricity). Absent = 1. */
  factor?: number;
}

/** `existing` in finalize's 409 `already_commissioned` body (billing-api finalize-guard.ts). */
export interface ExistingCommission {
  record_id: string;
  realm: string;
  meter_serial: string;
  asset_id: string | null;
  asset_type: string;
  committed_at: string | null;
}

/** Why retire refused: the old asset is still configured here. */
export interface RetireBlocker {
  kind: 'pocket' | 'virtual_meter' | 'common_area' | 'allocation';
  id: string;
  label: string;
}

export interface RetireCommissionResult {
  dry_run: boolean;
  retired: boolean;
  record: {
    id: string;
    realm: string;
    meter_serial: string;
    asset_id: string | null;
    asset_type: string;
    committed_at: string | null;
  };
  effects: {
    meter_config_rows: number;
    tariff_ids: string[];
    readings_committed: number;
    readings_pending: number;
  };
}

export interface CommissioningRecord {
  id: string;
  realm: string;
  meter_serial: string;
  asset_type: string;
  asset_id: string | null;
  status: 'pending_review' | 'committed' | 'failed';
  source: 'nfc' | 'ocr' | 'manual' | 'email';
  created_by: string | null;
  created_at: string;
  committed_at: string | null;
  error: string | null;
  needs_cf_wiring: 0 | 1;
}

// One row in the email-in commissioning queue. source_payload carries the
// building hint / OCR fields / xlsx batch context — shape varies per input
// type so the SPA inspects loosely.
export interface PendingCommissioning {
  id: string;
  realm: string;
  meter_serial: string;
  asset_type: string;
  status: 'pending_review';
  source: 'nfc' | 'ocr' | 'manual' | 'email';
  source_payload: string | { input_type?: 'xlsx' | 'ocr' | 'body'; building_hint?: string; client?: string; type_hint?: string | null; extracted?: Record<string, unknown> } | null;
  image_r2_key: string | null;
  created_by: string | null;
  created_at: string;
  error: string | null;
}

export interface ParentAssetMatch {
  asset_id: string;
  asset_type: string;
  realm: string;
  parent_id: string | null;
  name: string;
  parent_path: Array<{ id: string; name: string; type: string }>;
}

export interface CommissioningProfile {
  id: string;
  realm: string;
  name: string;
  asset_type: string;
  parent_id: string;
  route_key?: string | null;
  schema_id?: number | null;
  scale?: number | null;
  created_at?: string;
  created_by?: string;
}

export interface BillableMeter {
  meter_asset_id: string;
  meter_name: string;
  tariff_name: string;
  effective_from: string;
}

export const api = new ApiClient();
